// frontend/src/pages/RetrainingPage.tsx
// PRC-033: Modelos y actualizaciones con el sistema común (PRC-032): estado del
// sistema, flujo de procesos y programación en español; el estado se dice con
// texto y etiqueta, nunca con un botón deshabilitado. Sin cambios de lógica.

import React from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import OperationsDetailHeader from '../components/Operations/OperationsDetailHeader';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';
import { StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';
import { JourneyButton, JourneyCard, JourneyChip, JourneyText, type ChipTone } from '../modules/pricingJourney/components/JourneyBasics';
import { HeaderTag } from '../modules/pricingJourney/components/PageHeader';
import { J } from '../modules/pricingJourney/journeyTokens';

import {
    getSystemPulse,
    startRetraining,
    SystemPulseResponse,
    SystemModuleStatus
} from '../services/optimizationService';

const POLLING_INTERVAL = 10000;

// Nombres de negocio de cada proceso (docs/pricing/32_SISTEMA_VISUAL.md, glosario).
const MODULES: Array<{ key: keyof SystemPulseResponse['modules']; title: string; text: string }> = [
    { key: 'DATA_INGEST', title: 'Carga de datos', text: 'Lee e integra tus ventas, productos y costes.' },
    { key: 'PRICING_ENGINE', title: 'Motor de precios', text: 'Aprende de tu histórico para recomendar precios.' },
    { key: 'DEMAND_FORECAST', title: 'Previsión de demanda', text: 'Calcula la demanda prevista para Planificación.' },
    { key: 'FUTURE_SIMULATION', title: 'Simulación de escenarios', text: 'Proyecta el efecto de los precios en volumen e ingresos.' },
];

const moduleTitle = (key: string) => MODULES.find((m) => m.key === key)?.title ?? key.replace(/_/g, ' ').toLowerCase();

const statusView = (status: SystemModuleStatus | undefined, locked: boolean): { label: string; tone: ChipTone } => {
    if (locked) return { label: 'Incluido en Growth', tone: 'amber' };
    if (status?.status === 'running') return { label: 'En curso', tone: 'info' };
    if (status?.status === 'success') return { label: 'Completado', tone: 'ok' };
    if (status?.status === 'error' || status?.status === 'failed') return { label: 'Con error', tone: 'danger' };
    return { label: 'Pendiente', tone: 'neutral' };
};

const formatWhen = (date: string | null | undefined) => (date ? format(parseISO(date), "d MMM, HH:mm", { locale: es }) : '—');

const RetrainingPage: React.FC = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const { data: systemPulse, isLoading } = useQuery<SystemPulseResponse>({
        queryKey: ['systemPulse'],
        queryFn: getSystemPulse,
        refetchInterval: POLLING_INTERVAL,
    });

    const { mutate: triggerCycle, isPending: isTriggering } = useMutation({
        mutationFn: startRetraining,
        onSuccess: () => {
            enqueueSnackbar('Actualización de los modelos iniciada.', { variant: 'info' });
            queryClient.invalidateQueries({ queryKey: ['systemPulse'] });
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.detail || error.message || 'No se ha podido iniciar la actualización.';
            enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error', autoHideDuration: 6000 });
        }
    });

    const header = (
        <OperationsDetailHeader
            eyebrow="Operaciones · Modelos"
            title="Modelos y actualizaciones"
            description="Estado de los modelos, los procesos que los actualizan y su programación."
            action={systemPulse ? <HeaderTag>Plan {systemPulse.tier || 'Discovery'}</HeaderTag> : undefined}
        />
    );

    // PRC-031: estructura al instante en lugar de una rueda.
    if (isLoading) {
        return (
            <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 7 }}>
                {header}
                <PageSkeleton variant="cards" bare padded={false} />
            </Box>
        );
    }

    const tier = systemPulse?.tier || 'Discovery';
    const isLocked = (key: string) => tier === 'Discovery' && (key === 'DEMAND_FORECAST' || key === 'FUTURE_SIMULATION');
    const isProcessing = systemPulse?.global_status === 'processing';
    const updateAvailable = !isProcessing
        && (systemPulse?.smart_action?.reason === 'NEW_DATA_AVAILABLE' || systemPulse?.smart_action?.reason === 'COLD_START');

    return (
        <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 7 }}>
            {header}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Estado del sistema: lo dice el texto y la etiqueta; la acción solo aparece si hay algo que hacer. */}
                {isProcessing && (
                    <StatusBlock
                        variant="loading"
                        chip={<JourneyChip tone="info">En curso · suele tardar entre 60 y 120 minutos</JourneyChip>}
                        title="Actualizando los modelos"
                        text="El motor de precios está aprendiendo de tus últimas ventas y costes. Puedes seguir usando la aplicación; los resultados nuevos aparecerán al terminar."
                    />
                )}
                {updateAvailable && (
                    <StatusBlock
                        variant="warning"
                        chip={<JourneyChip tone="amber">Hay datos nuevos</JourneyChip>}
                        title="Puedes actualizar los modelos"
                        text="Hemos detectado cambios recientes en tus datos. Al actualizar, las recomendaciones de precio y la previsión usarán la información más reciente. Suele tardar entre 60 y 120 minutos."
                        actions={(
                            <JourneyButton onClick={() => triggerCycle()} disabled={isTriggering}>
                                {isTriggering ? 'Iniciando…' : 'Actualizar los modelos'}
                            </JourneyButton>
                        )}
                    />
                )}
                {!isProcessing && !updateAvailable && (
                    <StatusBlock
                        variant="success"
                        chip={<JourneyChip tone="ok">Al día</JourneyChip>}
                        title="Los modelos están al día"
                        text="Trabajan con la versión más reciente de tus datos. Cuando cargues datos nuevos, podrás actualizarlos desde aquí."
                    />
                )}

                {/* Flujo de procesos */}
                <Box>
                    <JourneyText variant="h3" component="h2" sx={{ mb: 1.5 }}>Flujo de procesos</JourneyText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0,1fr))' }, gap: 1.5 }}>
                        {MODULES.map((module, index) => {
                            const status = systemPulse?.modules[module.key];
                            const locked = isLocked(module.key);
                            const view = statusView(status, locked);
                            return (
                                <Box key={module.key} sx={{ position: 'relative' }}>
                                    <JourneyCard tight sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '8px', opacity: locked ? 0.75 : 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                            <Box component="span" sx={{ color: J.muted, fontSize: 12, fontWeight: 700 }}>Paso {index + 1}</Box>
                                            <JourneyChip tone={view.tone}>{view.label}</JourneyChip>
                                        </Box>
                                        <JourneyText variant="h3">{module.title}</JourneyText>
                                        <JourneyText variant="small">{module.text}</JourneyText>
                                        <Box sx={{ mt: 'auto', color: J.muted, fontSize: 12 }}>
                                            {locked
                                                ? 'Se activa al contratar Growth.'
                                                : status?.last_run_at
                                                    ? `Última vez ${formatDistanceToNow(parseISO(status.last_run_at), { addSuffix: true, locale: es })}`
                                                    : 'Aún no se ha ejecutado'}
                                        </Box>
                                    </JourneyCard>
                                    {index < MODULES.length - 1 && (
                                        <ArrowForwardRoundedIcon aria-hidden sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: J.borderStrong, zIndex: 1 }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Programación */}
                <Box>
                    <JourneyText variant="h3" component="h2" sx={{ mb: 1.5 }}>Programación</JourneyText>
                    <TableContainer sx={{ border: `1px solid ${J.border}`, borderRadius: '12px', overflowX: 'auto' }}>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: J.border, color: J.body, py: 1.4 }, '& .MuiTableCell-head': { color: J.muted, fontWeight: 700, fontSize: 12.5, bgcolor: 'rgba(148,163,184,.06)' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Proceso</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell>Última ejecución</TableCell>
                                    <TableCell>Próxima ejecución</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {systemPulse && Object.entries(systemPulse.modules).map(([key, mod]) => {
                                    const locked = isLocked(key);
                                    const view = statusView(mod, locked);
                                    return (
                                        <TableRow key={key}>
                                            <TableCell sx={{ color: `${J.text} !important`, fontWeight: 700 }}>{moduleTitle(key)}</TableCell>
                                            <TableCell><JourneyChip tone={view.tone}>{view.label}</JourneyChip></TableCell>
                                            <TableCell>{formatWhen(mod.last_run_at)}</TableCell>
                                            <TableCell>
                                                {locked ? 'Al contratar Growth' : mod.next_scheduled_run ? formatWhen(mod.next_scheduled_run) : 'Cuando la pidas'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    );
};

export default RetrainingPage;
